import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import useQuery from 'hooks/useQuery'
import {
  filterEmptyValues,
  getDepartmentOptions,
  getFamiliesOptionsBySection,
  getSectionOptionsByDepartment,
  noneField,
} from 'utils/helpers'
import { Button, Dropdown, DurationInput, TextInput } from 'components/common'
import { PRICING_FORM_FIELDS } from 'utils/constants'
import { DateTime } from 'components/common/input/dateTime'
import AggregatorsDropdown from 'components/masterData/aggregatorsDropdown'
import { InputOptions } from 'types/input'

const {
  ITEM_NUMBER,
  STORE_ID,
  BARCODE,
  DEPARTMENT,
  ERP_ITEM_DESC,
  ERP_UOM,
  FAMILY,
  ONLINE_DESC,
  SECTION,
  SUB_FAMILY,
  SAP_DEPARTMENT,
  SAP_SECTION,
  SAP_FAMILY,
  SAP_SUB_FAMILY,
  SPECIAL_PRICE_START_DATE,
  SPECIAL_PRICE_END_DATE,
  SLASH_PRICE_START_DATE,
  SLASH_PRICE_END_DATE,
  PROMO,
  PROMO_START_DATE,
  PROMO_END_DATE,
  LAST_PRICE_UPDATE,
  AGGREGATOR_ID,
  BLOCK_SALE_ON_POS,
} = PRICING_FORM_FIELDS

type Props = {
  toggleFilterForm: () => void
}

export const FILTER_FIELD_PRICING = 'pricingFilters'

const blockSaleOnPosOptions: InputOptions[] = [
  noneField,
  { label: 'Yes', value: true },
  { label: 'No', value: false },
]

const PricingFilterForm = ({ toggleFilterForm }: Props) => {
  const { getQuery, removeQuery, setMultipleQueries } = useQuery()
  const filters = getQuery(FILTER_FIELD_PRICING)
  const { register, handleSubmit, setValue, reset, watch } = useForm({
    defaultValues: filters,
  })
  const [resetFilter, setResetFilter] = useState(false)

  const onFilterApply = (data: any) => {
    if (data?.dept === 'NONE') {
      delete data.dept
    }

    if (data?.section === 'NONE') {
      delete data.section
    }

    if (data?.family === 'NONE') {
      delete data.family
    }

    if (data?.sapDept === 'NONE') {
      delete data.sapDept
    }

    if (data?.sapSection === 'NONE') {
      delete data.sapSection
    }

    if (data?.sapFamily === 'NONE') {
      delete data.sapFamily
    }

    if (data?.promo === 'NONE') {
      delete data.promo
    }

    if (data?.blockSaleOnPos === 'NONE') {
      delete data.blockSaleOnPos
    }

    const filteredData = filterEmptyValues(data)

    if (Object.values(filteredData).length > 0) {
      setMultipleQueries({ [FILTER_FIELD_PRICING]: filteredData, page: 1 })
    } else {
      removeQuery(FILTER_FIELD_PRICING)
    }

    toggleFilterForm()
  }

  // will watch the change of Department and Section Fields
  const deptWatch = watch('dept')
  const sectionWatch = watch('section')

  // Department Options
  const deptOptions = useMemo(() => getDepartmentOptions(), [])

  //  Section Options
  const sectionOptions = useMemo(() => {
    const dept = deptWatch || deptOptions[0]?.value

    return getSectionOptionsByDepartment(dept)
  }, [deptWatch, getSectionOptionsByDepartment])

  //  Family Options
  const familyOptions = useMemo(() => {
    const section = sectionWatch || sectionOptions[0]?.value

    return getFamiliesOptionsBySection(section)
  }, [sectionWatch, deptWatch, sectionOptions])

  // The SAP hierarchy cascades off the SAP lookup tables, so it needs its own
  // watches and option lists rather than sharing the legacy ones.
  const sapDeptWatch = watch(SAP_DEPARTMENT.key)
  const sapSectionWatch = watch(SAP_SECTION.key)

  const sapDeptOptions = useMemo(() => getDepartmentOptions(true), [])

  const sapSectionOptions = useMemo(() => {
    const dept = sapDeptWatch || sapDeptOptions[0]?.value

    return getSectionOptionsByDepartment(dept, true)
  }, [sapDeptWatch, sapDeptOptions])

  const sapFamilyOptions = useMemo(() => {
    const section = sapSectionWatch || sapSectionOptions[0]?.value

    return getFamiliesOptionsBySection(section, true)
  }, [sapSectionWatch, sapDeptWatch, sapSectionOptions])

  // Discards unsaved edits and closes the panel without touching applied filters
  const cancelFilter = () => {
    reset(filters)
    setResetFilter((pre) => !pre)
    toggleFilterForm()
  }

  useEffect(() => {
    reset(filters)
  }, [])

  return (
    <div className="mt-2 rounded-lg border bg-white px-5 py-3 ">
      <div className="mx-auto max-w-5xl">
        <form onSubmit={handleSubmit(onFilterApply)}>
          <div className="grid grid-cols-2 gap-x-14 gap-y-3 md:grid-cols-3">
            {/* Store Id Number */}
            <TextInput
              register={register}
              name={STORE_ID.key}
              label={STORE_ID.label}
            />
            {/*Aggregators */}
            <AggregatorsDropdown
              defaultSelected={filters?.[AGGREGATOR_ID.key]}
              resetForm={resetFilter}
              setValue={setValue}
              name={AGGREGATOR_ID.key}
              label="Aggregator"
              isActive
            />
            {/* Item Number */}
            <DurationInput
              register={register}
              startText="From"
              endText="To"
              title={ITEM_NUMBER.label}
            />
            {/* Barcode */}
            <TextInput
              register={register}
              name={BARCODE.key}
              label={BARCODE.label}
            />
            {/* Department */}
            <Dropdown
              name={DEPARTMENT.key}
              label={DEPARTMENT.label}
              options={deptOptions}
              setValue={setValue}
              defaultValue={filters?.dept || resetFilter}
            />
            {/* Section */}
            <Dropdown
              name={SECTION.key}
              label={SECTION.label}
              options={sectionOptions}
              setValue={setValue}
              defaultValue={filters?.section || sectionOptions?.[1]}
            />
            {/* Family */}
            <Dropdown
              name={FAMILY.key}
              label={FAMILY.label}
              options={familyOptions}
              setValue={setValue}
              defaultValue={filters?.family || familyOptions?.[1]}
            />
            {/* Sub Family */}
            <TextInput
              name={SUB_FAMILY.key}
              label={SUB_FAMILY.label}
              register={register}
            />
            {/* SAP Department */}
            <Dropdown
              name={SAP_DEPARTMENT.key}
              label={SAP_DEPARTMENT.label}
              options={sapDeptOptions}
              setValue={setValue}
              defaultValue={filters?.sapDept || resetFilter}
            />
            {/* SAP Section */}
            <Dropdown
              name={SAP_SECTION.key}
              label={SAP_SECTION.label}
              options={sapSectionOptions}
              setValue={setValue}
              defaultValue={filters?.sapSection || sapSectionOptions?.[1]}
            />
            {/* SAP Family */}
            <Dropdown
              name={SAP_FAMILY.key}
              label={SAP_FAMILY.label}
              options={sapFamilyOptions}
              setValue={setValue}
              defaultValue={filters?.sapFamily || sapFamilyOptions?.[1]}
            />
            {/* SAP Sub Family */}
            <TextInput
              name={SAP_SUB_FAMILY.key}
              label={SAP_SUB_FAMILY.label}
              register={register}
            />
            {/* ERP Item Description */}
            <TextInput
              name={ERP_ITEM_DESC.key}
              label={ERP_ITEM_DESC.label}
              register={register}
            />
            {/* ERP UOM */}
            <TextInput
              name={ERP_UOM.key}
              label={ERP_UOM.label}
              register={register}
            />

            {/* Online Description */}
            <TextInput
              register={register}
              name={ONLINE_DESC.key}
              label={ONLINE_DESC.label}
            />

            {/* Block Sale on POS */}
            <Dropdown
              name={BLOCK_SALE_ON_POS.key}
              options={blockSaleOnPosOptions}
              label={BLOCK_SALE_ON_POS.label}
              defaultValue={filters?.[BLOCK_SALE_ON_POS.key]}
              setValue={setValue}
            />

            {/* Last Price Update */}
            <DateTime
              register={register}
              name={LAST_PRICE_UPDATE.key}
              label={LAST_PRICE_UPDATE.label}
            />

            {/* Slash Price Start Date */}
            <DateTime
              label={SLASH_PRICE_START_DATE.label}
              name={SLASH_PRICE_START_DATE.key}
              register={register}
            />

            {/* Slash Price End Date */}
            <DateTime
              label={SLASH_PRICE_END_DATE.label}
              name={SLASH_PRICE_END_DATE.key}
              register={register}
            />

            {/* SPECIAL_PRICE Start Date */}
            <DateTime
              label={SPECIAL_PRICE_START_DATE.label}
              name={SPECIAL_PRICE_START_DATE.key}
              register={register}
            />
            {/* SPECIAL_PRICE End Date */}
            <DateTime
              label={SPECIAL_PRICE_END_DATE.label}
              name={SPECIAL_PRICE_END_DATE.key}
              register={register}
            />
            {/* Promo Price */}
            <Dropdown
              setValue={setValue}
              options={[
                { label: '--- NONE ---', value: 'NONE', disabled: false },
                { label: 'Active', value: '1' },
                { label: 'Inactive', value: '0' },
              ]}
              defaultValue={`${filters?.[PROMO.key]}`}
              label={PROMO.label}
              name={PROMO.key}
            />

            {/* Promo Price Start Date */}
            <DateTime
              label={PROMO_START_DATE.label}
              name="promoStartDate"
              register={register}
            />
            {/* Promo Price End Date */}
            <DateTime
              label={PROMO_END_DATE.label}
              name="promoEndDate"
              register={register}
            />
          </div>

          <div className="my-4 flex gap-5">
            <Button buttonText="Apply Filters" className="w-32" />
            <Button
              type="button"
              buttonText="cancel"
              variant="outline"
              onClick={cancelFilter}
              className="w-32"
            />
          </div>
        </form>
      </div>
    </div>
  )
}

export default PricingFilterForm
